import { generatedImageReviewMessage } from "@/lib/agent/generatedImageReviewMessages";
import { logger } from "@/utils/logger";

type SendAgentMessage = (
  message: string,
  imageUrl?: string,
  imageUrls?: string[],
  options?: {
    hideUserMessage?: boolean;
    assistantImageUrl?: string;
    assistantImageUrls?: string[];
    assistantImageSide?: "front" | "back";
    suppressThinkingTrace?: boolean;
    imageReviewToken?: string;
  }
) => Promise<void>;

// One review turn per render, ever. Each generated render has a unique URL, so
// a repeat (side, url) pair can only be a double-fire — completion paths that
// re-enter, or the callback being recreated mid-completion. The July session
// audit found the review turn duplicated back-to-back in 35 of the last 100
// sessions, polluting history and doubling the review LLM call. Module-level
// so the guard survives React re-renders and callback recreation.
const sentReviewKeys = new Set<string>();

/**
 * Creates a callback to notify the agent when image generation or modification completes.
 * Called from generation/modification completion paths (not useEffect) to avoid rate-limit spam.
 */
export function createNotifyAgentOfImageComplete(
  sendAgentMessage: SendAgentMessage
): (
  side: "front" | "back",
  imageUrl: string,
  variationUrls: string[],
  isModification: boolean,
  imageReviewToken?: string
) => void {
  return (side, imageUrl, variationUrls, isModification, imageReviewToken) => {
    // isModification is part of the key: a modification pipeline can return
    // the same URL as the original render (in-place/cached result), and its
    // "Updated: …" confirmation must not be swallowed by the plain review's
    // dedupe entry.
    const reviewKey = `${side}:${isModification ? "mod" : "gen"}:${imageUrl}`;
    if (sentReviewKeys.has(reviewKey)) {
      logger.warn("Skipped duplicate image-review turn for the same render", {
        component: "notifyAgentOfImageComplete",
        metadata: { side, isModification },
      });
      return;
    }
    sentReviewKeys.add(reviewKey);
    const reviewPrompt = generatedImageReviewMessage(side, isModification);

    // The render is already shown in the transcript by the generation/
    // modification success path, so this review turn is text-only — the agent
    // still receives the image (positional args) to describe it, but we do NOT
    // re-attach it to the assistant message (which would duplicate the render).
    // The review grant covers exactly the selected render. Variations are
    // already represented in UI state and must not hitchhike into this exempt
    // model turn as additional, ungranted image inputs.
    sendAgentMessage(reviewPrompt, imageUrl, undefined, {
      hideUserMessage: true,
      ...(imageReviewToken && { imageReviewToken }),
      // The review render is already in the transcript; this echo turn should
      // show a single, in-place status line rather than a stack of near-
      // identical "I'll inspect the front render…" thinking-step placeholders.
      suppressThinkingTrace: true,
    }).catch(error => {
      // The send never went out — release the dedupe entry so a retry of this
      // render's review isn't permanently blocked by a transient failure.
      sentReviewKeys.delete(reviewKey);
      logger.error(`Failed to notify agent about ${side} image completion`, {
        component: "notifyAgentOfImageComplete",
        metadata: {
          error: error instanceof Error ? error.message : String(error),
          side,
        },
      });
    });
  };
}
